import { FramedIcon } from '@heidi/design-system/framed-icon';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@/lib/cn';
import { parseDateAsLocal } from '@/utils/dates';
import { useTranslations, type TranslationKeys } from '@/utils/translations';
import { AlertCircle, Calendar, Clock, Loader2, ChevronRight, Stethoscope } from 'lucide-react';
import { useRouter } from 'next/router';
import type { AppointmentItem } from '@heidi/integrations/ehr/contracts';

interface AppointmentsBlockProps {
  status: 'fetching' | 'completed' | 'error';
  date?: string;
  totalAppointments?: number;
  appointments?: AppointmentItem[];
  error?: string;
  className?: string;
}

/**
 * AppointmentsBlock - Calendar-based display of EHR appointments
 *
 * Visual states:
 * - FETCHING: Skeleton loading with pulse animation
 * - COMPLETED: Calendar tile + timeline of appointments
 * - ERROR: Error message
 */
export const AppointmentsBlock = ({
  status,
  date,
  totalAppointments,
  appointments,
  error,
  className,
}: AppointmentsBlockProps): JSX.Element => {
  const router = useRouter();
  const { t, locale } = useTranslations();

  const handleSessionClick = (sessionId: string) => {
    router.push(`/scribe?sessionId=${sessionId}`);
  };

  // Parse date components
  const getDateParts = (dateStr?: string) => {
    if (!dateStr) return { dayNum: 0, dayName: '', monthName: '', isToday: false };
    try {
      const dateObj = parseDateAsLocal(dateStr);
      if (!dateObj) {
        return { dayNum: 0, dayName: '', monthName: '', isToday: false };
      }
      const userLocale = locale || undefined;
      return {
        dayNum: dateObj.getDate(),
        dayName: dateObj.toLocaleDateString(userLocale, { weekday: 'short' }),
        monthName: dateObj.toLocaleDateString(userLocale, { month: 'short' }),
        isToday: new Date().toDateString() === dateObj.toDateString(),
      };
    } catch {
      return { dayNum: 0, dayName: '', monthName: '', isToday: false };
    }
  };

  const dateParts = getDateParts(date);

  // Fetching state
  if (status === 'fetching') {
    return (
      <div
        className={cn(
          'flex w-full gap-4 rounded-xl border border-border/50 bg-surface p-4 duration-300 animate-in fade-in',
          className
        )}
      >
        <div className="flex size-16 shrink-0 animate-pulse flex-col items-center justify-center rounded-lg bg-background-secondary">
          <div className="mb-1 h-3 w-8 rounded bg-border/50" />
          <div className="h-6 w-6 rounded bg-border/50" />
        </div>
        <div className="flex flex-1 flex-col gap-3">
          <div className="flex items-center gap-2">
            <Loader2 className="size-4 animate-spin text-brand-primary" />
            <TypographyP2 className="text-text-secondary">
              {date ? t('chat.appointments.fetchingForDate' as TranslationKeys, { date }) : t('chat.appointments.fetching' as TranslationKeys)}
            </TypographyP2>
          </div>
          <div className="space-y-2">
            <div className="h-12 animate-pulse rounded-lg bg-background-secondary" />
            <div className="h-12 animate-pulse rounded-lg bg-background-secondary" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div
        className={cn(
          'flex w-full gap-4 rounded-xl border border-destructive/20 bg-destructive/5 p-4 duration-300 animate-in fade-in',
          className
        )}
      >
        <FramedIcon size="lg" state="error" icon={<AlertCircle />} />
        <div className="flex flex-1 flex-col justify-center gap-1">
          <TypographyP2 className="font-medium text-text-primary">
            {t('chat.appointments.errorTitle' as TranslationKeys)}
          </TypographyP2>
          <TypographyCaption className="text-destructive">
            {error || t('chat.appointments.errorDefault' as TranslationKeys)}
          </TypographyCaption>
        </div>
      </div>
    );
  }

  // Empty state
  if (!appointments || appointments.length === 0) {
    return (
      <div
        className={cn(
          'flex w-full gap-4 rounded-xl border border-border/50 bg-surface p-4 duration-300 animate-in fade-in',
          className
        )}
      >
        <div className="flex size-16 shrink-0 flex-col items-center justify-center rounded-lg border border-border/50 bg-background-secondary">
          <span className="text-[10px] font-medium uppercase tracking-wider text-text-tertiary">
            {dateParts.monthName}
          </span>
          <span className="text-2xl font-semibold tabular-nums text-text-secondary">
            {dateParts.dayNum}
          </span>
        </div>
        <div className="flex flex-1 flex-col justify-center gap-1">
          <div className="flex items-center gap-2">
            <Calendar className="size-4 text-text-tertiary" />
            <TypographyP2 className="font-medium text-text-primary">
              {t('chat.appointments.noAppointments' as TranslationKeys)}
            </TypographyP2>
          </div>
          <TypographyCaption className="text-text-tertiary">
            {dateParts.isToday
              ? t('chat.appointments.noAppointmentsToday' as TranslationKeys)
              : t('chat.appointments.noAppointmentsOnDate' as TranslationKeys, {
                  dayName: dateParts.dayName,
                  monthName: dateParts.monthName,
                  dayNum: dateParts.dayNum,
                })}
          </TypographyCaption>
        </div>
      </div>
    );
  }

  // Completed state with appointments
  return (
    <div
      className={cn(
        'flex w-full gap-4 rounded-xl border border-border/50 bg-surface p-4 duration-300 animate-in fade-in',
        className
      )}
    >
      {/* Calendar tile */}
      <div
        className={cn(
          'flex size-16 shrink-0 flex-col items-center justify-center rounded-lg border',
          dateParts.isToday
            ? 'border-brand-primary/30 bg-brand-primary/10'
            : 'border-border/50 bg-background-secondary'
        )}
      >
        <span
          className={cn(
            'text-[10px] font-medium uppercase tracking-wider',
            dateParts.isToday ? 'text-brand-primary' : 'text-text-tertiary'
          )}
        >
          {dateParts.monthName}
        </span>
        <span
          className={cn(
            'text-2xl font-semibold tabular-nums',
            dateParts.isToday ? 'text-brand-primary' : 'text-text-primary'
          )}
        >
          {dateParts.dayNum}
        </span>
        <span
          className={cn(
            'text-[10px] font-medium',
            dateParts.isToday ? 'text-brand-primary' : 'text-text-tertiary'
          )}
        >
          {dateParts.dayName}
        </span>
      </div>

      {/* Appointments timeline */}
      <div className="flex flex-1 flex-col gap-1">
        <div className="mb-2 flex items-center justify-between">
          <TypographyCaption className="font-medium text-text-secondary">
            {dateParts.isToday
              ? t('chat.appointments.todaysSchedule' as TranslationKeys)
              : t('chat.appointments.dateSchedule' as TranslationKeys, {
                  dayName: dateParts.dayName,
                  monthName: dateParts.monthName,
                  dayNum: dateParts.dayNum,
                })}
          </TypographyCaption>
          <TypographyCaption className="text-text-tertiary">
            {t('chat.appointments.appointmentCount' as TranslationKeys, { count: totalAppointments ?? appointments.length })}
          </TypographyCaption>
        </div>

        <div className="relative flex flex-col">
          {appointments.length > 1 && (
            <div className="absolute bottom-4 left-[23px] top-4 w-px bg-border/50" />
          )}

          {appointments.map((appt) => {
            const isClickable = appt.has_heidi_session && appt.session_id;
            return (
              <button
                type="button"
                key={appt.record_id}
                onClick={() => isClickable && handleSessionClick(appt.session_id!)}
                disabled={!isClickable}
                className={cn(
                  'group relative flex items-start gap-3 rounded-lg px-1 py-2 text-left transition-all',
                  isClickable && 'cursor-pointer hover:bg-surface-hover',
                  !isClickable && 'cursor-default'
                )}
              >
                {/* Time marker */}
                <div className="relative z-10 flex w-11 shrink-0 flex-col items-center">
                  <div
                    className={cn(
                      'flex size-5 items-center justify-center rounded-full border-2 transition-colors',
                      appt.has_heidi_session
                        ? 'border-validation-success bg-validation-success-muted'
                        : 'border-border bg-surface',
                      isClickable && 'group-hover:border-brand-primary group-hover:bg-brand-primary/10'
                    )}
                  >
                    {appt.has_heidi_session ? (
                      <Stethoscope className="size-2.5 text-validation-success" />
                    ) : (
                      <Clock className="size-2.5 text-text-tertiary" />
                    )}
                  </div>
                  <span className="mt-0.5 text-[11px] font-medium tabular-nums text-text-tertiary">
                    {appt.appointment_time}
                  </span>
                </div>

                {/* Appointment content */}
                <div className="flex min-w-0 flex-1 flex-col gap-0.5 pt-0.5">
                  <span className="truncate font-medium text-text-primary">
                    {appt.patient_name || t('chat.appointments.unknownPatient' as TranslationKeys)}
                  </span>
                  <div className="flex items-center gap-2">
                    {appt.duration_minutes && (
                      <TypographyCaption className="text-text-tertiary">
                        {t('chat.appointments.minDuration' as TranslationKeys, { minutes: appt.duration_minutes })}
                      </TypographyCaption>
                    )}
                    {appt.has_heidi_session && (
                      <span className="rounded-full bg-validation-success-muted px-2 py-0.5 text-[10px] font-medium text-validation-success">
                        {t('chat.appointments.heidiSession' as TranslationKeys)}
                      </span>
                    )}
                  </div>
                </div>

                {isClickable && (
                  <ChevronRight className="mt-0.5 size-4 shrink-0 text-text-tertiary opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100 rtl:-scale-x-100 rtl:group-hover:-translate-x-0.5" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
